import { Card, CardBody } from 'react-bootstrap';
import { t } from 'react-switch-lang';
import Image from 'next/image';
import Decimal from 'decimal.js';
import Button from '../Button';
import styles from '../../styles/PaymentOption.module.scss';
import { events } from '../../utils/Amplitude';
import { getText } from '../../utils/Helpers';
import ReplaceTextWithElement from '../ReplaceTextWithElement';
import { FormatAmount } from '../../utils/Validations';

function formatPercentage(number) {
  if (!Decimal.isDecimal(number)) return null;
  const percentage = number.mul(100);
  if (percentage.mod(1).isZero()) {
    return `${percentage.toFixed(1)}%`;
  }
  return `${percentage}%`;
}

/**
 * @param {import('../../context/PaymentProcessContext').FeeV2} fee
 * @param {number} amount
 */
const calculateFee = (fee, amount) => {
  const tiers = fee?.Tiers;
  if (Number.isNaN(amount)) return null;
  if (tiers?.length === 0) return null;
  const pAmount = new Decimal(amount);
  const reversedTiers = tiers.slice().reverse();
  const applicableTier = reversedTiers.find((tier) => pAmount.gte(new Decimal(tier.Amount)));
  const percentage = new Decimal(applicableTier.Percent);
  const fixed = new Decimal(applicableTier.Fixed);

  const totalFee = pAmount.mul(percentage).add(fixed).toDecimalPlaces(2).toNumber();

  let feeString = FormatAmount(totalFee);
  if (percentage > 0 && fixed > 0) {
    feeString = `${feeString} (${formatPercentage(percentage)} + ${FormatAmount(fixed.toNumber())})`;
  } else if (percentage > 0) {
    feeString = `${feeString} (${formatPercentage(percentage)})`;
  }
  return {
    totalFee,
    feeString,
  };
};

const poImagesSrc = {
  Credit: {
    height: 32,
    images: [
      {
        path: '/images/logos/CreditCards/Visa.png',
        alt: 'Visa_ImageDescription',
      },
      {
        path: '/images/logos/CreditCards/Mastercard.png',
        alt: 'Mastercard_ImageDescription',
      },
      {
        path: '/images/logos/CreditCards/Amex.png',
        alt: 'Amex_ImageDescription',
      },
      {
        path: '/images/logos/CreditCards/UnionPay.png',
        alt: 'UnionPay_ImageDescription',
      },
    ],
  },
  InPerson: {
    height: 32,
    images: [
      {
        path: '/images/logos/CanadaPost.png',
        alt: 'AvailableAtCanadaPostLogo_ImageDescription',
      },
    ],
  },
  eTransfer: {
    height: 34,
    images: [
      {
        path: '/images/logos/eTransfer.png',
        alt: 'ETransfer_ImageDescription',
      },
    ],
  },
  PayPal: {
    height: 30,
    images: [
      {
        path: '/images/logos/PayPal.png',
        alt: 'PayPalLogo_ImageDescription',
      },
    ],
  },
  PeoplesRFM: {
    height: 34,
    images: [
      {
        path: '/images/logos/eTransfer.png',
        alt: 'ETransfer_ImageDescription',
      },
    ],
  },
};

function PaymentOptionImages({ paymentOptionName }) {
  const imgObj = poImagesSrc[paymentOptionName];
  return (
    <div className={styles.poImagesContainer}>
      {imgObj?.images?.map(({ path, alt }) => (
        <Image
          src={path}
          key={path}
          alt={t(alt)}
          style={{ objectFit: 'contain', width: 'auto', height: imgObj.height, marginInline: '3px' }}
          sizes="(max-width: 768px) 100vw, 50vw"
          height={0}
          width={0}
        />
      ))}
    </div>
  );
}

/**
 * @param {object} props
 * @param {import('../../context/PaymentProcessContext').PaymentOption} props.paymentOptionInfo
 */
export default function PaymentOptionCard({
  paymentOptionInfo,
  paymentAmount,
  onSelect,
}) {
  const {
    Name,
    Title,
    Description1,
    Description2,
    Delivery,
    FeeV2,
    Blurb2,
    Enabled,
  } = paymentOptionInfo;
  const amount = Number(paymentAmount);
  const calculatedFee = calculateFee(FeeV2, amount);

  // If the calculateFee return null, that means the tiers for FeeV2 are not configured properly,
  // in that case, not showing the payment option at all.
  if (!calculatedFee) return null;
  return (
    <Button
      className={styles.poCardButton}
      ampEvent={events.USER_SELECTED_PAYMENT_OPTION}
      ampEventProps={{ PaymentOption: Name }}
      onClick={() => onSelect(paymentOptionInfo, calculatedFee.totalFee)}
      ariaLabel={t('PaymentOption_ButtonDescription').replace('xPaymentOption', getText(Title))}
      disabled={!Enabled}
      role="tab"
    >
      <Card className={`${styles.poContainer} ${!Enabled && styles.poContainerDisabled}`}>
        <CardBody className={styles.poContent}>
          <div className={styles.poData}>
            <h2>
              <ReplaceTextWithElement
                text={getText(Title)}
                replaceWith={<i>Interac</i>}
                replaceKey="Interac"
              />
            </h2>
            <div className={styles.poDescriptions}>
              {!Enabled ?
                <p>{getText(Blurb2)}</p> : (
                  <>
                    <p>{getText(Description1)}</p>
                    <p>{getText(Description2)}</p>
                  </>
                )}
            </div>
            <h3 className={styles.poProcessingTime}>
              <b>{`${t('PaymentOption_ProcessingTime_Title')}: `}</b>
              {getText(Delivery)}
            </h3>
            <h3 className={styles.poFee}>
              <b>{`${t('PaymentOption_Fee_Title')}: `}</b>
              {calculatedFee?.feeString}
            </h3>
          </div>
          <PaymentOptionImages paymentOptionName={Name} />
        </CardBody>
      </Card>
    </Button>
  );
}
